/**
 * Oeffentliche Angebotsseite (Stitch: angebot_catering_gourmet) — OHNE Login.
 * Route /angebot/:token; laedt GET /api/public/offer/:token (same-origin, der
 * Host traegt den Mandanten) und sendet die Wahl an POST .../respond.
 */
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import type { PublicOffer } from "../ui/types";
import { formatDate, money } from "../ui/format";
import { Icon } from "../ui/components";
// LMIV: Allergen-Codes + Legende gehören VOR die Kaufentscheidung des Kunden (sql/137).
import { lmivLabel, DIETARY } from "../ui/lmiv";

type Phase = "loading" | "ready" | "submitting" | "done" | "error";

/** Deko-Fotos je Options-Karte (Design-Vorlage: docs/design/DESIGN-SYSTEM.md), zyklisch je Index. */
const OPTION_PHOTOS = ["/img/offer-1-v2.webp", "/img/offer-2-v2.webp", "/img/offer-3-v2.webp"];

/** Rubrik-Labels fuer die neuen offer_items-Sektionen, je Sprache. */
const SECTION_LABEL: Record<"de" | "en", Record<string, string>> = {
  de: { menu: "Menü", drinks: "Getränke", equipment: "Equipment", staff: "Personal", other: "Weiteres" },
  en: { menu: "Menu", drinks: "Drinks", equipment: "Equipment", staff: "Staff", other: "Other" },
};

/**
 * Statische UI-Texte DE/EN (Block 4: nur DE/EN, kein IT/FR laut Antoine). Gesteuert über
 * `offer.event.offer_language` — sobald die Daten geladen sind, ist die gesamte Seite (bis auf
 * den Vor-Lade-Fehlerzustand, der die Sprache noch nicht kennt) in der gewählten Sprache.
 */
const T = {
  de: {
    heroLabel: "Ihr persönliches Angebot",
    heroFallbackTitle: "Ihre Veranstaltung",
    guestsSuffix: (n: number) => `${n} Gäste`,
    validUntil: (date: string) => `gültig bis ${date}`,
    offerNumberPrefix: (num: string) => `Angebot Nr. ${num} · `,
    intro: (dateText: string, guestText: string) => `Wir freuen uns sehr, Ihnen folgendes Angebot${dateText}${guestText} präsentieren zu dürfen.`,
    introForDate: (date: string) => ` für Ihre Feier am ${date}`,
    doneTitle: "Vielen Dank für Ihre Auswahl!",
    doneBody: "Wir haben Ihre Entscheidung erhalten und melden uns umgehend mit den nächsten Schritten bei Ihnen.",
    optionLabel: (label: string) => `Option ${label}`,
    perPerson: "pro Person",
    total: "gesamt",
    selectedBadge: "Ausgewählt",
    optionalTag: "(optional)",
    notesHeading: "Haben Sie noch Anmerkungen?",
    notesPlaceholder: "Teilen Sie uns gerne besondere Wünsche, Allergien oder Fragen mit …",
    agbLabel: "Ich nehme dieses Angebot verbindlich an und akzeptiere die genannten Zahlungsbedingungen.",
    ctaSubmitting: "Wird gesendet …",
    ctaAccept: "Dieses Angebot annehmen",
    hintChooseOption: "Bitte wählen Sie zuerst eine Option aus.",
    hintAcceptAgb: "Bitte bestätigen Sie die verbindliche Annahme, um fortzufahren.",
    footerTagline: "Premium Catering & Hospitality",
    menuHeading: "Finale Menüauswahl",
    menuBody: "Bitte bestätigen Sie kurz Ihre finale Menüauswahl, damit wir alles rechtzeitig für Sie vorbereiten können.",
    menuCta: "Menü verbindlich bestätigen",
    menuConfirmedAt: (date: string) => `Menü bestätigt am ${date}`,
    depositHeading: "Anzahlung",
    depositBody: (amount: string) => `Zur Bestätigung Ihrer Buchung bitten wir um eine Anzahlung von ${amount}.`,
    depositCta: "Jetzt Anzahlung bezahlen",
    depositPaid: (amount: string) => `Anzahlung über ${amount} erhalten – vielen Dank!`,
    depositNoLink: "Die Zahlungsinformationen erhalten Sie in Kürze separat von Ihrem Ansprechpartner.",
    expiredHeading: "Dieses Angebot ist abgelaufen",
    expiredBody: "Die Gültigkeitsfrist dieses Angebots ist verstrichen. Gerne erstellen wir Ihnen ein aktualisiertes Angebot – bitte kontaktieren Sie uns einfach.",
    errExpired: "Dieses Angebot ist inzwischen abgelaufen und kann nicht mehr angenommen werden.",
    errConflict: "Dieses Angebot wurde soeben bereits verbindlich angenommen. Bitte laden Sie die Seite neu.",
    // CX-Audit 2026-07-17: Fehler-/Ablauf-Karten sagten „kontaktieren Sie uns", boten aber
    // keinerlei Kontaktweg — der Chat ist der einzige loginfreie Kanal auf derselben Domain.
    contactCta: "Nachricht über den Chat senden",
    allergenHeading: "Allergene (LMIV)",
    allergenNone: "ohne deklarationspflichtige Allergene",
    allergenOnRequest:
      "Zu einzelnen Positionen liegen noch keine Allergenangaben vor – Informationen zu Allergenen und Unverträglichkeiten erhalten Sie jederzeit auf Anfrage.",
  },
  en: {
    heroLabel: "Your personal offer",
    heroFallbackTitle: "Your event",
    guestsSuffix: (n: number) => `${n} guests`,
    validUntil: (date: string) => `valid until ${date}`,
    offerNumberPrefix: (num: string) => `Offer no. ${num} · `,
    intro: (dateText: string, guestText: string) => `We are delighted to present the following offer${dateText}${guestText} to you.`,
    introForDate: (date: string) => ` for your celebration on ${date}`,
    doneTitle: "Thank you for your selection!",
    doneBody: "We have received your decision and will be in touch shortly with the next steps.",
    optionLabel: (label: string) => `Option ${label}`,
    perPerson: "per person",
    total: "total",
    selectedBadge: "Selected",
    optionalTag: "(optional)",
    notesHeading: "Any comments or requests?",
    notesPlaceholder: "Feel free to share special requests, allergies, or questions …",
    agbLabel: "I bindingly accept this offer and agree to the stated payment terms.",
    ctaSubmitting: "Sending …",
    ctaAccept: "Accept this offer",
    hintChooseOption: "Please select an option first.",
    hintAcceptAgb: "Please confirm the binding acceptance to proceed.",
    footerTagline: "Premium Catering & Hospitality",
    menuHeading: "Final menu selection",
    menuBody: "Please take a moment to confirm your final menu selection so we can prepare everything for you in time.",
    menuCta: "Confirm menu",
    menuConfirmedAt: (date: string) => `Menu confirmed on ${date}`,
    depositHeading: "Deposit",
    depositBody: (amount: string) => `To confirm your booking, we kindly ask for a deposit of ${amount}.`,
    depositCta: "Pay deposit now",
    depositPaid: (amount: string) => `Deposit of ${amount} received – thank you!`,
    depositNoLink: "You will receive the payment details separately from your contact person shortly.",
    expiredHeading: "This offer has expired",
    expiredBody: "The validity period of this offer has passed. We would be happy to prepare an updated offer for you – please just get in touch.",
    errExpired: "This offer has since expired and can no longer be accepted.",
    errConflict: "This offer has just been bindingly accepted. Please reload the page.",
    contactCta: "Send us a message via chat",
    allergenHeading: "Allergens (EU FIC)",
    allergenNone: "no declarable allergens",
    allergenOnRequest:
      "Allergen details for some items are not yet on file – information on allergens and intolerances is available on request at any time.",
  },
} as const;

/** Passendes Material-Symbol je Menue-Rubrik (rein dekorativ, Fallback: restaurant). */
function sectionIcon(section: string): string {
  const s = section.toLowerCase();
  if (s.includes("vorspeise") || s.includes("suppe") || s.includes("gruß") || s.includes("starter") || s.includes("soup")) return "soup_kitchen";
  if (s.includes("haupt") || s.includes("main")) return "set_meal";
  if (s.includes("dessert") || s.includes("nachspeise") || s.includes("süß") || s.includes("sweet")) return "icecream";
  if (s.includes("getränk") || s.includes("wein") || s.includes("bar") || s.includes("aperitif") || s.includes("drink") || s.includes("wine"))
    return "wine_bar";
  if (s.includes("zwischen") || s.includes("interm")) return "local_dining";
  return "restaurant";
}

export default function PublicOfferPage() {
  const { token } = useParams<{ token: string }>();
  const [offer, setOffer] = useState<PublicOffer | null>(null);
  const [phase, setPhase] = useState<Phase>("loading");
  const [selected, setSelected] = useState<string | null>(null);
  const [notes, setNotes] = useState("");
  const [agbAccepted, setAgbAccepted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [menuBusy, setMenuBusy] = useState(false);

  useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        const res = await fetch(`/api/public/offer/${token}`);
        if (!res.ok) throw new Error(res.status === 404 ? "Dieses Angebot ist nicht (mehr) verfügbar." : "Fehler beim Laden.");
        const body = (await res.json().catch(() => {
          throw new Error("Fehler beim Laden. Bitte versuchen Sie es später erneut.");
        })) as { data: PublicOffer };
        setOffer(body.data);
        const chosen = body.data.options.find((o) => o.is_chosen);
        // "paid" zaehlt wie "offer_chosen" als verbindlich angenommen (Block 6: Menü-Bestaetigung
        // bleibt bis zur Durchfuehrung moeglich, auch nach vollstaendiger Zahlung).
        if (chosen) {
          setSelected(chosen.id);
          setPhase(["offer_chosen", "paid"].includes(body.data.event.status) ? "done" : "ready");
        } else {
          setPhase("ready");
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
        setPhase("error");
      }
    })();
  }, [token]);

  /** Block 6: finale Menüauswahl verbindlich bestätigen. */
  async function confirmMenu() {
    if (!token) return;
    setMenuBusy(true);
    setError(null);
    try {
      const res = await fetch(`/api/public/offer/${token}/confirm-menu`, { method: "POST" });
      if (!res.ok) throw new Error("Die Bestätigung konnte nicht gespeichert werden. Bitte erneut versuchen.");
      const body = (await res.json()) as { data: { confirmedAt: string } };
      setOffer((prev) => (prev ? { ...prev, event: { ...prev.event, menu_confirmed_at: body.data.confirmedAt } } : prev));
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setMenuBusy(false);
    }
  }

  async function accept() {
    if (!token || !selected || !agbAccepted) return;
    setPhase("submitting");
    setError(null);
    try {
      const res = await fetch(`/api/public/offer/${token}/respond`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ optionId: selected, notes: notes || undefined, agbAccepted: true }),
      });
      if (res.status === 410) {
        // H-16: waehrend der Sitzung abgelaufen -> Flag setzen, die Annahme-UI weicht der
        // Ablauf-Karte (der Server hat nichts mutiert).
        setOffer((prev) => (prev ? { ...prev, event: { ...prev.event, offer_expired: true } } : prev));
        throw new Error(t.errExpired);
      }
      // H-17: parallele zweite Annahme (zweiter Tab) hat den Claim verloren -> neu laden.
      if (res.status === 409) throw new Error(t.errConflict);
      if (!res.ok) throw new Error("Die Auswahl konnte nicht gespeichert werden. Bitte erneut versuchen.");
      // H-15: Der Response traegt den gerade erzeugten Anzahlungslink - vorher wurde der Body
      // verworfen und der Kunde bekam seinen Zahlungsweg nie zu sehen.
      const body = (await res.json().catch(() => null)) as {
        data?: { depositUrl?: string | null; depositCents?: number | null };
      } | null;
      const depositUrl = body?.data?.depositUrl ?? null;
      const depositCents = body?.data?.depositCents ?? null;
      setOffer((prev) =>
        prev
          ? {
              ...prev,
              deposit:
                depositUrl && depositCents
                  ? { amount_cents: depositCents, status: "processing", checkout_url: depositUrl }
                  : prev.deposit ?? null,
            }
          : prev,
      );
      setPhase("done");
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setPhase("ready");
    }
  }

  const tenantLabel = window.location.hostname.split(".")[0] ?? window.location.hostname;
  const lang: "de" | "en" = offer?.event.offer_language === "en" ? "en" : "de";
  const t = T[lang];
  const dateLocale = lang === "en" ? "en-GB" : "de-DE";
  const sectionLabel = SECTION_LABEL[lang];
  // H-16: abgelaufen -> Angebot bleibt lesbar, aber ohne Annahme-UI (Server lehnt ohnehin mit 410 ab).
  const expired = offer?.event.offer_expired === true;

  // LMIV-Legende: alle im Angebot vorkommenden Allergen-Codes + Hinweis, falls Speise-/
  // Getraenke-Positionen ohne gepflegte Angaben dabei sind (Info „auf Anfrage" statt Schweigen).
  const publicItems = offer?.options.flatMap((o) => o.items ?? []) ?? [];
  const usedAllergenCodes = [
    ...new Set(publicItems.flatMap((it) => (it.section !== "adjustment" && Array.isArray(it.allergens) ? it.allergens : []))),
  ].sort();
  const hasUnresolvedAllergens = publicItems.some(
    (it) => (it.section === "menu" || it.section === "drinks") && it.allergens == null,
  );
  const hasFoodItems = publicItems.some((it) => it.section === "menu" || it.section === "drinks");

  return (
    <div className="flex min-h-screen flex-col bg-surface font-sans text-on-background antialiased dark:bg-inverse-surface dark:text-inverse-on-surface">
      {/* Kopfbalken (Design-Vorlage 2e): Betriebs-Logo + Name. Kontaktzeile folgt, sobald die
          Public-API Firmenkontakt liefert (aktuell nicht Teil von get_public_offer). */}
      <div className="flex items-center gap-3 border-b border-outline-variant/40 bg-surface-container-lowest px-margin-mobile py-3.5 dark:border-outline/30 dark:bg-inverse-surface md:px-margin-desktop">
        <div className="grid h-[30px] w-[30px] shrink-0 place-items-center rounded-[9px] bg-primary font-serif text-[16px] font-bold text-on-primary">
          {tenantLabel.charAt(0).toUpperCase()}
        </div>
        <div className="truncate text-[14px] font-bold tracking-[0.04em] text-on-surface dark:text-inverse-on-surface">{tenantLabel}</div>
      </div>
      {/* Gebrandeter Einladungs-Kopf mit Hero-Foto (Design-Vorlage: docs/design/DESIGN-SYSTEM.md) */}
      <header className="relative h-72 overflow-hidden md:h-[330px]">
        <img
          src="/img/hero-v2.webp"
          alt=""
          className="absolute inset-0 h-full w-full object-cover object-[50%_60%]"
        />
        <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(24,19,13,.5),rgba(24,19,13,.72))]" />
        <div className="relative flex h-full flex-col items-center justify-center gap-3 px-margin-mobile text-center md:px-margin-desktop">
          <p className="font-label-md text-label-md uppercase tracking-[0.22em] text-[#F5EFE4]/75">
            {t.heroLabel}
          </p>
          <h1 className="font-serif text-display-lg-mobile text-[#FBF7EE] md:text-display-lg">
            {offer?.event.number ?? t.heroFallbackTitle}
          </h1>
          {offer && (
            <p className="max-w-2xl font-body-md text-body-md text-[#F5EFE4]/85">
              {offer.event.event_date ? formatDate(offer.event.event_date, dateLocale) : ""}
              {offer.event.guest_count ? ` · ${t.guestsSuffix(offer.event.guest_count)}` : ""}
            </p>
          )}
          {offer?.event.offer_valid_until && (
            <div className="mt-1 inline-flex items-center gap-2 rounded-full border border-white/25 bg-white/10 px-3.5 py-1.5 font-label-md text-label-md text-[#FBF7EE]">
              {offer.event.number ? t.offerNumberPrefix(offer.event.number) : ""}
              {t.validUntil(formatDate(offer.event.offer_valid_until, dateLocale))}
            </div>
          )}
        </div>
      </header>
      {offer && (
        <div className="border-b border-outline-variant/30 bg-surface-container-lowest px-margin-mobile py-8 text-center dark:border-outline/30 dark:bg-inverse-surface md:px-margin-desktop">
          <p className="mx-auto max-w-2xl font-body-lg text-body-lg text-secondary dark:text-secondary-fixed-dim">
            {t.intro(
              offer.event.event_date ? t.introForDate(formatDate(offer.event.event_date, dateLocale)) : "",
              offer.event.guest_count ? ` (${t.guestsSuffix(offer.event.guest_count)})` : "",
            )}
          </p>
        </div>
      )}

      <main className="mx-auto w-full max-w-container-max flex-grow px-margin-mobile py-12 md:px-margin-desktop">
        <div className="mx-auto max-w-5xl space-y-12">
          {phase === "loading" && (
            <>
              <div className="h-64 animate-pulse rounded-xl bg-surface-container-high dark:bg-surface-variant/20" />
              <div className="h-64 animate-pulse rounded-xl bg-surface-container-high dark:bg-surface-variant/20" />
            </>
          )}

          {phase === "error" && (
            <div className="mx-auto max-w-2xl rounded-xl bg-error-container p-10 text-center text-on-error-container">
              <Icon name="link_off" className="text-5xl" />
              <p className="mt-4 font-headline-sm text-headline-sm">{error}</p>
              <p className="mt-2 font-body-sm text-body-sm opacity-80">
                Bitte wenden Sie sich direkt an Ihren Ansprechpartner.
              </p>
              {/* CX-Audit 2026-07-17: vorher Sackgasse ohne jeden Kontaktweg. */}
              <a
                href="/chat"
                className="mt-5 inline-flex items-center gap-2 rounded-lg bg-surface-container-lowest px-4 py-2.5 font-body-sm text-body-sm font-semibold text-on-surface shadow-sm transition-shadow hover:shadow-md dark:bg-inverse-surface dark:text-inverse-on-surface"
              >
                <Icon name="chat" className="text-[18px]" /> {t.contactCta}
              </a>
            </div>
          )}

          {phase === "done" && offer && (
            <div className="mx-auto max-w-2xl rounded-xl border border-outline-variant/30 bg-surface-container-lowest p-10 text-center shadow-sm dark:border-outline/30 dark:bg-inverse-surface">
              <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-primary/10 text-primary dark:bg-primary-fixed-dim/10 dark:text-primary-fixed-dim">
                <Icon name="celebration" className="text-4xl" />
              </div>
              <h2 className="mt-6 font-headline-md text-headline-md text-on-surface dark:text-inverse-on-surface">
                {t.doneTitle}
              </h2>
              <p className="mx-auto mt-3 max-w-md font-body-md text-body-md text-on-surface-variant dark:text-secondary-fixed-dim">
                {t.doneBody}
              </p>

              {/* H-15: Anzahlung - der beim Annehmen erzeugte Zahlungsweg wird angezeigt (und
                  bleibt dank Persistenz an der Zahlungszeile auch nach Reload abrufbar). */}
              {offer.deposit && (
                <div className="mx-auto mt-8 max-w-md rounded-xl border border-outline-variant/30 bg-surface p-6 text-left dark:border-outline/30 dark:bg-inverse-surface">
                  <h3 className="font-headline-sm text-headline-sm text-on-surface dark:text-inverse-on-surface">{t.depositHeading}</h3>
                  {offer.deposit.status === "succeeded" ? (
                    <p className="mt-3 flex items-center gap-2 font-body-sm text-body-sm text-on-surface-variant dark:text-secondary-fixed-dim">
                      <Icon name="check_circle" className="text-[20px] text-primary dark:text-primary-fixed-dim" />
                      {t.depositPaid(money(offer.deposit.amount_cents))}
                    </p>
                  ) : offer.deposit.checkout_url ? (
                    <>
                      <p className="mt-2 font-body-sm text-body-sm text-on-surface-variant dark:text-secondary-fixed-dim">
                        {t.depositBody(money(offer.deposit.amount_cents))}
                      </p>
                      <a
                        href={offer.deposit.checkout_url}
                        target="_blank"
                        rel="noreferrer"
                        className="btn-primary mt-4 flex w-full items-center justify-center gap-2 !py-3"
                      >
                        <Icon name="credit_card" className="text-[18px]" /> {t.depositCta}
                      </a>
                    </>
                  ) : (
                    <p className="mt-2 font-body-sm text-body-sm text-on-surface-variant dark:text-secondary-fixed-dim">{t.depositNoLink}</p>
                  )}
                </div>
              )}

              {/* Block 6: Menü-Bestätigungs-Workflow */}
              <div className="mx-auto mt-8 max-w-md rounded-xl border border-outline-variant/30 bg-surface p-6 text-left dark:border-outline/30 dark:bg-inverse-surface">
                <h3 className="font-headline-sm text-headline-sm text-on-surface dark:text-inverse-on-surface">{t.menuHeading}</h3>
                {offer.event.menu_confirmed_at ? (
                  <p className="mt-3 flex items-center gap-2 font-body-sm text-body-sm text-on-surface-variant dark:text-secondary-fixed-dim">
                    <Icon name="check_circle" className="text-[20px] text-primary dark:text-primary-fixed-dim" />
                    {t.menuConfirmedAt(formatDate(offer.event.menu_confirmed_at, dateLocale))}
                  </p>
                ) : (
                  <>
                    <p className="mt-2 font-body-sm text-body-sm text-on-surface-variant dark:text-secondary-fixed-dim">{t.menuBody}</p>
                    {error && (
                      <p className="mt-3 rounded-lg bg-error-container px-4 py-3 font-body-sm text-body-sm text-on-error-container">{error}</p>
                    )}
                    <button
                      type="button"
                      className="btn-primary mt-4 w-full !py-3"
                      disabled={menuBusy}
                      onClick={() => void confirmMenu()}
                    >
                      {menuBusy ? t.ctaSubmitting : t.menuCta}
                    </button>
                  </>
                )}
              </div>
            </div>
          )}

          {(phase === "ready" || phase === "submitting") && offer && (
            <>
              <div className="grid grid-cols-1 items-stretch gap-5 md:grid-cols-3">
                {offer.options.map((o, idx) => {
                  const ms = o.menu_selection ?? {};
                  const isSel = selected === o.id;
                  const perPerson = o.guest_count > 0 ? Math.round(o.amount_total_cents / o.guest_count) : null;
                  // Positionen nach Rubrik gruppieren: bevorzugt die echten offer_items,
                  // sonst Fallback auf die (aeltere) menu_selection-Konvention.
                  // LMIV: Allergen-Codes + Diaet-Kennzeichnung je Position mitfuehren.
                  interface PublicLine {
                    name: string;
                    allergens?: string[] | null;
                    dietary?: Record<string, boolean> | null;
                  }
                  const groups = new Map<string, PublicLine[]>();
                  if (o.items && o.items.length > 0) {
                    for (const it of o.items) {
                      if (it.section === "adjustment") continue; // in der Summe enthalten
                      const label = sectionLabel[it.section] ?? it.section;
                      // Mehrsprachigkeit: Angebotssprache zeigt die uebersetzten Namen
                      // (eingefroren im Snapshot), Fallback ist der Originalname.
                      const baseName = it.translations?.[lang]?.name ?? it.name;
                      // H-21: optionale Positionen sind NICHT im Gesamtpreis enthalten — kennzeichnen.
                      const name = it.is_optional ? `${baseName} ${t.optionalTag}` : baseName;
                      groups.set(label, [...(groups.get(label) ?? []), { name, allergens: it.allergens, dietary: it.dietary }]);
                    }
                  } else {
                    for (const item of ms.items ?? []) {
                      groups.set(item.section, [...(groups.get(item.section) ?? []), { name: item.name }]);
                    }
                  }
                  return (
                    <button
                      key={o.id}
                      type="button"
                      onClick={() => setSelected(o.id)}
                      aria-pressed={isSel}
                      className="group block h-full text-left focus:outline-none"
                    >
                      <div
                        className={`relative flex h-full flex-col overflow-hidden rounded-2xl border bg-surface-container-lowest shadow-sm transition-all duration-300 dark:bg-inverse-surface ${
                          isSel ? "border-2 border-primary shadow-lg" : "border-surface-variant hover:shadow-md dark:border-outline/30"
                        }`}
                      >
                        {/* F7: Eckbadge bei Auswahl */}
                        {isSel && (
                          <span className="absolute right-3.5 top-3.5 z-10 rounded-full bg-primary px-2.5 py-1 font-label-md text-label-md uppercase tracking-wider text-on-primary">
                            {t.selectedBadge}
                          </span>
                        )}

                        {/* Foto oben (Design-Vorlage 2e: 168px) */}
                        <div className="relative h-[168px] shrink-0 overflow-hidden">
                          <img
                            src={OPTION_PHOTOS[idx % OPTION_PHOTOS.length]}
                            alt=""
                            className="absolute inset-0 h-full w-full object-cover"
                          />
                        </div>

                        {/* Inhalt */}
                        <div className="flex flex-1 flex-col gap-3 p-5">
                          <span className="font-label-md text-label-md uppercase tracking-wider text-secondary dark:text-secondary-fixed-dim">
                            {t.optionLabel(o.label)}
                          </span>
                          {/* F7: Radio oben statt Häkchen unten */}
                          <div className="flex items-center gap-2.5">
                            <span
                              className={`grid h-5 w-5 shrink-0 place-items-center rounded-full border-2 transition-colors ${
                                isSel ? "border-primary" : "border-outline dark:border-outline"
                              }`}
                            >
                              {isSel && <span className="h-2.5 w-2.5 rounded-full bg-primary" />}
                            </span>
                            <h2 className="font-serif text-headline-md text-on-surface dark:text-inverse-on-surface">
                              {o.title || ms.title || `Variante ${o.label}`}
                            </h2>
                          </div>

                          {ms.description && (
                            <p className="font-body-sm text-body-sm text-on-surface-variant dark:text-secondary-fixed-dim">
                              {ms.description}
                            </p>
                          )}

                          {groups.size > 0 && (
                            <div className="space-y-3">
                              {[...groups.entries()].map(([section, lines]) => (
                                <div key={section} className="flex gap-3">
                                  <Icon name={sectionIcon(section)} className="mt-0.5 text-[18px] text-outline dark:text-outline" />
                                  <div className="min-w-0">
                                    <h4 className="font-label-md text-label-md uppercase tracking-wider text-on-surface dark:text-inverse-on-surface">
                                      {section}
                                    </h4>
                                    {/* LMIV: je Position eine Zeile mit Allergen-Codes (Legende unter
                                        den Karten) + Diaet-Kennzeichnung — Pflichtinfo VOR der Annahme. */}
                                    {lines.map((line, li) => {
                                      const diet = DIETARY.filter((d) => line.dietary?.[d.key] === true);
                                      return (
                                        <p key={li} className="mt-0.5 font-body-sm text-body-sm text-on-surface-variant dark:text-secondary-fixed-dim">
                                          {line.name}
                                          {Array.isArray(line.allergens) && line.allergens.length > 0 && (
                                            <span className="text-on-surface-variant/70 dark:text-secondary-fixed-dim/70">
                                              {" "}({[...line.allergens].sort().join(", ")})
                                            </span>
                                          )}
                                          {diet.length > 0 && (
                                            <span className="text-primary dark:text-primary-fixed-dim">
                                              {" "}· {diet.map((d) => (lang === "en" ? d.labelEn : d.label)).join(" · ")}
                                            </span>
                                          )}
                                        </p>
                                      );
                                    })}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}

                          {/* F6: Preis-Accent nur bei Auswahl */}
                          <div className="mt-auto flex items-baseline justify-between border-t border-surface-variant pt-3.5 dark:border-outline/30">
                            {perPerson != null ? (
                              <span
                                className={`font-headline-sm text-headline-sm ${
                                  isSel ? "text-primary dark:text-primary-fixed-dim" : "text-on-surface dark:text-inverse-on-surface"
                                }`}
                              >
                                {money(perPerson)}
                                <span className="ml-1 font-label-md text-label-md normal-case tracking-normal text-secondary dark:text-secondary-fixed-dim">
                                  {t.perPerson}
                                </span>
                              </span>
                            ) : (
                              <span
                                className={`font-headline-sm text-headline-sm ${
                                  isSel ? "text-primary dark:text-primary-fixed-dim" : "text-on-surface dark:text-inverse-on-surface"
                                }`}
                              >
                                {money(o.amount_total_cents)}
                              </span>
                            )}
                            <span className="font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">
                              {money(o.amount_total_cents)} {t.total}
                            </span>
                          </div>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>

              {/* LMIV-Legende: Codes ausschreiben + „auf Anfrage"-Hinweis bei ungepflegten
                  Positionen. Pflichtinformation VOR der verbindlichen Annahme. */}
              {hasFoodItems && (
                <div className="rounded-xl border border-outline-variant/30 bg-surface-container-lowest p-6 shadow-sm dark:border-outline/30 dark:bg-inverse-surface">
                  <h4 className="flex items-center gap-2 font-label-md text-label-md uppercase tracking-wider text-on-surface dark:text-inverse-on-surface">
                    <Icon name="info" className="text-[16px] text-outline dark:text-outline" /> {t.allergenHeading}
                  </h4>
                  {usedAllergenCodes.length > 0 ? (
                    <p className="mt-2 font-body-sm text-body-sm text-on-surface-variant dark:text-secondary-fixed-dim">
                      {usedAllergenCodes.map((code) => `${code} = ${lmivLabel(code, lang)}`).join(" · ")}
                    </p>
                  ) : !hasUnresolvedAllergens ? (
                    <p className="mt-2 font-body-sm text-body-sm text-on-surface-variant dark:text-secondary-fixed-dim">{t.allergenNone}</p>
                  ) : null}
                  {hasUnresolvedAllergens && (
                    <p className="mt-2 font-body-sm text-body-sm text-on-surface-variant dark:text-secondary-fixed-dim">{t.allergenOnRequest}</p>
                  )}
                </div>
              )}

              {/* H-16: abgelaufen -> nur noch lesend, Ablauf-Karte statt Anmerkungen + CTA. */}
              {expired ? (
                <div className="rounded-xl border border-outline-variant/30 bg-surface-container-lowest p-8 text-center shadow-sm dark:border-outline/30 dark:bg-inverse-surface">
                  <Icon name="schedule" className="text-[32px] text-outline dark:text-outline" />
                  <h3 className="mt-3 font-headline-sm text-headline-sm text-on-surface dark:text-inverse-on-surface">
                    {t.expiredHeading}
                  </h3>
                  <p className="mx-auto mt-3 max-w-xl font-body-md text-body-md text-secondary dark:text-secondary-fixed-dim">
                    {t.expiredBody}
                  </p>
                  {/* CX-Audit 2026-07-17: „kontaktieren Sie uns einfach" hatte keinen Kontaktweg.
                      Bevorzugt echte Kontaktdaten (E-Mail/Telefon aus tenant_settings, sql/136),
                      der Chat bleibt als loginfreier Kanal immer dabei. */}
                  <div className="mt-5 flex flex-wrap items-center justify-center gap-3">
                    {offer.contact?.company_email && (
                      <a href={`mailto:${offer.contact.company_email}`} className="btn-primary inline-flex">
                        <Icon name="mail" className="text-[18px]" /> {offer.contact.company_email}
                      </a>
                    )}
                    {offer.contact?.company_phone && (
                      <a href={`tel:${offer.contact.company_phone.replace(/[^\d+]/g, "")}`} className="btn-secondary inline-flex">
                        <Icon name="call" className="text-[18px]" /> {offer.contact.company_phone}
                      </a>
                    )}
                    <a href="/chat" className={offer.contact?.company_email ? "btn-secondary inline-flex" : "btn-primary inline-flex"}>
                      <Icon name="chat" className="text-[18px]" /> {t.contactCta}
                    </a>
                  </div>
                </div>
              ) : (
              <div className="rounded-xl border border-outline-variant/30 bg-surface-container-lowest p-8 shadow-sm dark:border-outline/30 dark:bg-inverse-surface">
                <h3 className="font-headline-sm text-headline-sm text-on-surface dark:text-inverse-on-surface">
                  {t.notesHeading}
                </h3>
                <textarea
                  rows={4}
                  className="input mt-6 resize-none"
                  placeholder={t.notesPlaceholder}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                />
                <label className="mt-6 flex items-start gap-3 font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">
                  <input
                    type="checkbox"
                    className="mt-0.5"
                    checked={agbAccepted}
                    onChange={(e) => setAgbAccepted(e.target.checked)}
                  />
                  <span>{t.agbLabel}</span>
                </label>
                {error && (
                  <p className="mt-4 rounded-lg bg-error-container px-4 py-3 font-body-sm text-body-sm text-on-error-container">
                    {error}
                  </p>
                )}
                <button
                  type="button"
                  className="btn-primary mt-8 w-full !py-4"
                  disabled={!selected || !agbAccepted || phase === "submitting"}
                  onClick={() => void accept()}
                >
                  {phase === "submitting" ? t.ctaSubmitting : t.ctaAccept}
                  <Icon name="arrow_forward" className="text-[18px]" />
                </button>
                {!selected ? (
                  <p className="mt-3 text-center font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">
                    {t.hintChooseOption}
                  </p>
                ) : !agbAccepted ? (
                  <p className="mt-3 text-center font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">
                    {t.hintAcceptAgb}
                  </p>
                ) : null}
              </div>
              )}
            </>
          )}
        </div>
      </main>

      {/* Footer — CX-Audit 2026-07-17: zeigt jetzt den echten Betriebskontakt (token-gebunden
          aus der Public-API, sql/136), sobald gepflegt; sonst wie zuvor nur Name + Tagline. */}
      <footer className="mt-12 border-t border-surface-variant bg-surface-container-high px-margin-mobile py-10 dark:border-outline/30 dark:bg-surface-dim/20 md:px-margin-desktop">
        <div className="mx-auto flex max-w-5xl flex-col items-center justify-between gap-8 text-center md:flex-row md:text-left">
          <div className="flex items-center gap-4 text-secondary dark:text-secondary-fixed-dim">
            <span className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-primary/14 text-primary dark:text-primary-fixed-dim">
              <Icon name="verified" className="text-2xl" />
            </span>
            <div>
              <p className="font-label-md text-label-md uppercase tracking-wider text-on-surface dark:text-inverse-on-surface">
                {offer?.contact?.company_name ?? tenantLabel}
              </p>
              <p className="font-body-sm text-body-sm">{t.footerTagline}</p>
              {(offer?.contact?.company_email || offer?.contact?.company_phone) && (
                <p className="mt-1.5 flex flex-wrap items-center justify-center gap-x-4 gap-y-1 font-body-sm text-body-sm md:justify-start">
                  {offer.contact?.company_email && (
                    <a href={`mailto:${offer.contact.company_email}`} className="inline-flex items-center gap-1.5 hover:text-primary hover:underline">
                      <Icon name="mail" className="text-[15px]" /> {offer.contact.company_email}
                    </a>
                  )}
                  {offer.contact?.company_phone && (
                    <a href={`tel:${offer.contact.company_phone.replace(/[^\d+]/g, "")}`} className="inline-flex items-center gap-1.5 hover:text-primary hover:underline">
                      <Icon name="call" className="text-[15px]" /> {offer.contact.company_phone}
                    </a>
                  )}
                </p>
              )}
            </div>
          </div>
          <p className="font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">
            powered by MAESTRO
          </p>
        </div>
      </footer>
    </div>
  );
}
